/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {useEffect} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {OVERVIEW_API_PARAM, overviewApiMode} from '../../util/overviewApi';

/**
 * Keeps the mock switch visible in the address bar. The choice itself lives in
 * storage, so it survives navigation either way — but a developer working
 * against the mock should be able to see that from the URL of whichever page
 * they are on, and not wonder later why the dashboard shows figures the backend
 * never returned.
 *
 * Only `mock` is advertised: `backend` is the default, so leaving that in the
 * URL would be noise, and a stale `?overviewApi=backend` on a shared link reads
 * like a setting when it is really just the normal state.
 *
 * Renders nothing, and does nothing at all outside dev builds — `overviewApiMode`
 * is hard-wired to `backend` there.
 */
export default function OverviewApiUrlSync() {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Merge into the live URL, not into the router's idea of it: views that
        // canonicalise their own query do it with history.replaceState, which
        // useLocation never sees. Rebuilding from the stale value would drop
        // whatever they just wrote (the invoice requests list lost its
        // page/limit/status that way).
        const params = new URLSearchParams(window.location.search);
        const inUrl = params.get(OVERVIEW_API_PARAM);
        const wanted = overviewApiMode() === 'mock' ? 'mock' : null;
        if (inUrl === wanted) return;
        if (wanted === null) params.delete(OVERVIEW_API_PARAM);
        else params.set(OVERVIEW_API_PARAM, wanted);
        const search = params.toString();
        navigate(
            {
                pathname: window.location.pathname,
                search: search ? `?${search}` : '',
                hash: window.location.hash
            },
            {replace: true}
        );
    }, [location.pathname, location.search, location.hash]);

    return null;
}
